import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  Search,
  Filter,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  Mail,
  Building,
  User,
  MapPin,
  Phone,
  UserCheck,
  GraduationCap,
  Languages,
  Briefcase,
  Award,
  CreditCard,
  Baby,
  CheckCircle,
  ChevronDown,
  Check,
  X,
  ArrowLeftCircle,
  ChevronUp,
  ArrowLeft,
} from "lucide-react";
import { getPayoutApplicationsList, getCandidateById, updatePayoutDetails, updateClaimStatus, updateTenureStatus } from "../../services/candidateapi";
import { toast } from "react-hot-toast";
import { getJobs } from "../../services/jobsapi";
import { useRbac } from "../../context";
import { getMySubordinates } from "../../services/usersapi";
import { datePickerStyles } from "../../components/constants/styles";
import { getStatusBadgeColor, showConfirmation } from "../../components/utils/application.utils";
import ExcelDownloadButton from "../../components/exceldownloadforjoinedcandidates";
import type { Application, PaginationState, UserType } from "../../components/types/application.types";
import { cn } from "../../components/lib/utils"; 
import { motion, useAnimation } from "framer-motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

interface Candidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  highestQualification: string;
  totalExperienceYears: number;
  totalExperienceMonths: number;
  skills: string[];
  currentLocationDetails: {
    city: string;
    fullAddress: string;
  };
  profileSource: string;
  createdAt: string;
  candidateProfileStrength: number;
  fatherName?: string;
  gender?: string;
  dateOfBirth?: string;
  aadharNumber?: string;
  jobCategory?: string;
  employmentType?: string;
  spokenLanguages?: string[];
  preferredJobCategories?: string[];
  preferredEmploymentTypes?: string[];
}

interface JobDetails {
  id: string;
  title: string;
  companyName: string;
  salary?: string;
  commissionReceived?: string;
  commissionPayable?: string;
  commissionPaid?: string;
}

interface ApplicationWithTenure extends Application {
  tenureDaysLeft?: number;
  tenureStatus?: boolean;
  verificationStatus?: string;
  employeeId?: string;
  job: JobDetails;
  isClaimed?: boolean;
  commClaimed?: number;
  joiningDate?: string;
  ledBy?: {
    id: string;   
    username: string;
  };
  commissionPayable?: number;
  commissionReceived?: number;
  salary?: number;
  position?: string;
  companyLocation?: string;
  companyName?: string;
}

export function PayoutCandidates() {  
  const navigate = useNavigate();
  const { user } = useRbac();
  const controls = useAnimation();

  const isAdmin = (user?.role as string) === "ADMIN" || (user?.role as string) === "SHM";

  useEffect(() => {
    if (typeof document !== "undefined" && datePickerStyles) {
      const styleSheet = document.createElement("style");
      styleSheet.innerText = datePickerStyles;
      document.head.appendChild(styleSheet);
      return () => {
        if (document.head.contains(styleSheet)) {
          document.head.removeChild(styleSheet);
        }
      };
    }
  }, []);

  const [applications, setApplications] = useState<ApplicationWithTenure[]>([]);
  const [payoutSummary, setPayoutSummary] = useState<{ commReceived?: number; commPayable?: number; commClaimed?: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  });
  const [showCandidateDetailsModal, setShowCandidateDetailsModal] = useState(false);
  const [selectedCandidateForDetails, setSelectedCandidateForDetails] = useState<Candidate | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<JobDetails[]>([]);
  const [recruiters, setRecruiters] = useState<UserType[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isLoadingRecruiters, setIsLoadingRecruiters] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [selectedJob, setSelectedJob] = useState<string>("all");
  const [selectedRecruiter, setSelectedRecruiter] = useState<string>("all");
  const [selectedDateFrom, setSelectedDateFrom] = useState<string>("");
  const [selectedDateTo, setSelectedDateTo] = useState<string>("");
  const [selectedClaimStatus, setSelectedClaimStatus] = useState<string>("all");
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [editingAppId, setEditingAppId] = useState<string>("");
  const [payoutForm, setPayoutForm] = useState({
    commissionPayable: "",
    commissionReceived: "",
    commissionPaid: "",
    comments: ""
  });
  const [showEmploymentModal, setShowEmploymentModal] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>("");
  const [employmentForm, setEmploymentForm] = useState({
    employeeId: "",
    positionName: "",
    salary: "",
    commission: "",
    commissionPaid: "",
  });
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimComment, setClaimComment] = useState("");
  const [claimingAppId, setClaimingAppId] = useState<string>("");
  const [claimAction, setClaimAction] = useState<"Claimed" | "Not Claimed">("Claimed");
  const isFetchingRef = useRef(false);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (!showClaimModal) {
      setTimeout(() => {
        document.body.style.removeProperty("pointer-events");
        document.body.removeAttribute("data-scroll-locked");
      }, 0);
    }
  }, [showClaimModal]);

  const jobOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Jobs" }];
    if (jobs.length > 0) {
      jobs.forEach((job) => {
        options.push({ value: job.id, label: job.title });
      });
    } else if (!isLoadingJobs) {
      options.push({ value: "no-jobs", label: "No jobs available", disabled: true });
    }
    return options;
  }, [jobs, isLoadingJobs]);

  const recruiterOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "All Recruiters" }];
    if (recruiters.length > 0) {
      recruiters.forEach((recruiter) => {
        options.push({ value: recruiter.id, label: recruiter.username });
      });
    } else if (!isLoadingRecruiters) {
      options.push({ value: "no-recruiters", label: "No recruiters available", disabled: true });
    }
    return options;
  }, [recruiters, isLoadingRecruiters]);

  const claimStatusOptions: SearchableSelectOption[] = useMemo(() => [
    { value: "all", label: "All Claims" },
    { value: "true", label: "Claimed" },
    { value: "false", label: "Not Claimed" },
  ], []);

  const handleCandidateNameClick = async (app: ApplicationWithTenure) => {
    console.log(app);
    setShowPayoutModal(false); // Close other modals
    setShowClaimModal(false);
    setShowEmploymentModal(false);
    setIsLoadingDetails(true);
    setDetailsError(null);
    setShowCandidateDetailsModal(true);

    try {
      const response = await getCandidateById(app.candidate.id);
      if (!response?.data?.candidate) {
        throw new Error("No candidate data returned from API");
      }
      const candidate: Candidate = response.data.candidate;
      setSelectedCandidateForDetails(candidate);
    } catch (err: any) {
      setDetailsError(err.message || "Failed to fetch candidate details");
      toast.error(err.message || "Failed to fetch candidate details", {
        duration: 4000,
      });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
      const newTimer = setTimeout(() => {
        setSearchTerm(value);
      }, 500);
      setSearchDebounceTimer(newTimer);
    },
    [searchDebounceTimer],
  );

  useEffect(() => {
    return () => {
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }
    };
  }, [searchDebounceTimer]);

  const fetchCandidates = useCallback(async () => {
    if (isFetchingRef.current) return;
    
    try {
      isFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      
      const response = await getPayoutApplicationsList({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        jobId: selectedJob !== "all" ? selectedJob : undefined,
        recruiterId: selectedRecruiter !== "all" ? selectedRecruiter : undefined,
        joiningDateFrom: selectedDateFrom || undefined,
        joiningDateTo: selectedDateTo || undefined,
        claimStatus: selectedClaimStatus !== "all" ? selectedClaimStatus : undefined,
        _t: Date.now(),
      });
      
      const apps = (response.data.applications ?? []).map((app: any) => {
        const isClaimed = !!app.isClaimed;
        const commPayable = Number(app.commissionPayable ?? app.job?.commissionPayable ?? 0);
        return {
          ...app,
          candidate: {
            id: app.candidateId ?? "",
            name: app.candidateName ?? "N/A",
            currentLocationDetails: { city: app.companyLocation ?? "N/A" },
            createdAt: app.joiningDate ?? null,
          },
          job: {
            id: app.jobId ?? "",
            title: app.position ?? "N/A",
            companyName: app.companyName ?? "N/A",
            salary: app.salary ?? "N/A",
            commissionReceived: app.commissionReceived ?? "N/A",
            commissionPayable: app.commissionPayable ?? "N/A",
          },
          commClaimed: isClaimed ? commPayable : 0,
          isClaimed,
          tenureDaysLeft: Math.floor(Math.random() * 30),
          tenureStatus: Math.random() > 0.5,
          verificationStatus: ["Pending", "Verified", "Rejected"][Math.floor(Math.random() * 3)],
        };
      });
      
      setApplications(apps);
      setPayoutSummary(response.summary || null);
      setPagination((prev) => ({
        ...prev,
        page: response.page  || 1,
        totalPages: response.totalPages || 1,
        totalResults: response.totalResults || apps.length,
      }));
      
    } catch (err: any) {
      setError(err.message || "Failed to fetch candidates");
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [
    pagination.page,
    pagination.limit,
    searchTerm,
    selectedJob,
    selectedRecruiter,
    selectedDateFrom,
    selectedDateTo,
    selectedClaimStatus,
  ]);

  useEffect(() => {
    fetchCandidates();
  }, [
    pagination.page,
    pagination.limit,
    searchTerm,
    selectedJob,
    selectedRecruiter,
    selectedDateFrom,
    selectedDateTo,
    selectedClaimStatus,
  ]);

  useEffect(() => {
    fetchJobs();
    fetchRecruiters();
  }, []);

  const fetchJobs = useCallback(async () => {
    setIsLoadingJobs(true);
    try {
      const response = await getJobs({
        page: 1,
        limit: 100,
        _t: Date.now(),
      });

      if (response?.data?.jobs) {
        setJobs(response.data.jobs.map((job: any) => ({
          id: job.id,
          title: job.title,
          companyName: job.companyName,
          salary: job.salary,
          commissionReceived: job.commissionReceived,
          commissionPayable: job.commissionPayable,
        })));
      } else {
        setJobs([]);
        // toast.error("No jobs found in the system.", {
        //   duration: 3000,
        // });
      }
    } catch (err: any) {
      console.error("Failed to fetch jobs:", err);
      setJobs([]);
      toast.error("Failed to load jobs for filtering");
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  const fetchRecruiters = useCallback(async () => {
    setIsLoadingRecruiters(true);
    try {
      const response = await getMySubordinates();
      if (response?.data?.subordinates) {
        setRecruiters(response.data.subordinates);
      } else {
        setRecruiters([]);
        // toast.error("No recruiters found in the system.", {
        //   duration: 3000,
        // });
      }
    } catch (err: any) {
      console.error("Failed to fetch recruiters:", err);
      setRecruiters([]);
      toast.error("Failed to load recruiters for filtering");
    } finally {
      setIsLoadingRecruiters(false);
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleSubmitPayout = async () => {
    try {
      await updatePayoutDetails(editingAppId, payoutForm);
      setApplications((apps) =>
        apps.map((app) => {
          if (app.applicationId === editingAppId) {
            return {
              ...app,
              job: {
                ...app.job,
                commissionPayable: payoutForm.commissionPayable,
                commissionReceived: payoutForm.commissionReceived,
                commissionPaid: payoutForm.commissionPaid,
              },
            };
          }
          return app;
        })
      );
      setShowPayoutModal(false);
      setEditingAppId("");
      setPayoutForm({ commissionPayable: "", commissionReceived: "", commissionPaid: "", comments: "" });
    } catch (error) {
      toast.error("Failed to update payout details");
    }
  };

  const handleEditPayoutClick = (e: React.MouseEvent, app: ApplicationWithTenure) => {
    e.stopPropagation(); // Prevent table row click
    setShowCandidateDetailsModal(false); // Close other modals
    setShowClaimModal(false);
    setShowEmploymentModal(false);
    setEditingAppId(app.applicationId);
    setPayoutForm({
      commissionPayable: app.job?.commissionPayable || "",
      commissionReceived: app.job?.commissionReceived || "",
      commissionPaid: app.job?.commissionPaid || "",
      comments: ""
    });
    setShowPayoutModal(true);
  };

  const handleEmploymentSubmit = async () => {
    try {
      const payoutDetails = {
        employeeIdAssignedByCompany: employmentForm.employeeId,
        finalPositionName: employmentForm.positionName,
        finalSalary: Number(employmentForm.salary),
        commissionReceivableFromCompany: Number(employmentForm.commission),
        commissionPayableToSourcer: Number(employmentForm.commissionPaid),
      };

      await updateTenureStatus(selectedApplicationId, {
        isTenureApproved: true,
        payoutDetails,
      });

      setApplications((apps) =>
        apps.map((app) =>
          app.applicationId === selectedApplicationId ? { ...app, tenureStatus: true } : app,
        ),
      );

      setEmploymentForm({
        employeeId: "",
        positionName: "",
        salary: "",
        commission: "",
        commissionPaid: "",
      });
      setShowEmploymentModal(false);
      setSelectedApplicationId("");
    } catch (error) {
      console.error("Failed to approve tenure with employment details:", error);
      toast.error("Failed to approve tenure");
    }
  };

  const handleClaimClick = (e: React.MouseEvent, applicationId: string, action: "Claimed" | "Not Claimed" = "Claimed") => {
    e.stopPropagation(); // Prevent table row click
    setShowCandidateDetailsModal(false); // Close other modals
    setShowPayoutModal(false);
    setShowEmploymentModal(false);
    setClaimingAppId(applicationId);
    setClaimAction(action);
    setShowClaimModal(true);
    setClaimComment("");
  };

  const handleSubmitClaim = async () => {
    try {
      await updateClaimStatus(claimingAppId, {
        isClaimed: claimAction === "Claimed",
        comments: claimComment,
      });
      
      setApplications((apps) =>
        apps.map((app) => {
          if (app.applicationId === claimingAppId) {
            const commPayable = Number(app.job?.commissionPayable) || 0;
            return {
              ...app,
              isClaimed: claimAction === "Claimed",
              commClaimed: claimAction === "Claimed" ? commPayable : 0,
            };
          }
          return app;
        })
      );

      setShowClaimModal(false);
      setClaimingAppId("");
      setClaimComment("");
      toast.success(`${claimAction} successfully!`);
      await fetchCandidates();
    } catch (error) {
      toast.error('Failed to update claim status');
    }
  };

  const getAppointmentsForExcel = async () => {
    try {
      let allApplications: any[] = [];
      let currentPage = 1;
      let totalPages = 1;

      while (currentPage <= totalPages) {
        const params = {
          page: currentPage,
          limit: 100,
          search: searchTerm || undefined,
          jobId: selectedJob !== "all" ? selectedJob : undefined,
          recruiterId: selectedRecruiter !== "all" ? selectedRecruiter : undefined,
          claimStatus: selectedClaimStatus !== "all" ? selectedClaimStatus : undefined,
          joiningDateFrom: selectedDateFrom || undefined,
          joiningDateTo: selectedDateTo || undefined,
          _t: Date.now(),
        };

        const response = await getPayoutApplicationsList(params);

        if (!response?.data?.applications) {
          break;
        }

        const apps = response.data.applications.map((app: any) => ({
          "Joining Date": app.joiningDate ? new Date(app.joiningDate).toLocaleDateString("en-GB") : "N/A",
          "Candidate Name": app.candidateName || "N/A",
          "Email": app.email || "N/A",
          "Company Name": app.companyName || "N/A",
          "Company Location": app.companyLocation || "N/A",
          "Employee ID": app.employeeId || "N/A",
          "Applied By": app.ledBy?.username || "N/A",
          "Position": app.position || "N/A",
          "Phone": app.phone || "N/A",
          "Salary": app.salary || "N/A",
          "Commission Received": app.commissionReceived || "N/A",
          "Commission Payable": app.commissionPayable || "N/A",
          "Commission Claimed": app.isClaimed ? "Yes" : "No",
          "Tenure Status": app.tenureStatus !== undefined ? (app.tenureStatus ? "Approved" : "Rejected") : "Pending",
          "Tenure Days Left": app.tenureDaysLeft ?? "N/A",
          "Verification Status": app.verificationStatus || "Unknown",
          "City": app.candidate?.currentLocationDetails?.city || app.companyLocation || "N/A",
        }));

        allApplications = [...allApplications, ...apps];
        totalPages = response.totalPages || 1;
        currentPage += 1;
      }

      return allApplications;
    } catch (err: any) {
      console.error("Failed to fetch applications for Excel:", err);
      toast.error(err.response?.data?.message || "Failed to fetch applications for export");
      return [];
    }
  };

  const filteredApplications = useMemo(() => {
    let filtered = applications;

    if (selectedDateFrom) {
      const fromDate = new Date(selectedDateFrom);
      filtered = filtered.filter(app => {
        if (!app.joiningDate) return false;
        const joiningDate = new Date(app.joiningDate);
        return joiningDate >= fromDate;
      });
    }
    if (selectedDateTo) {
      const toDate = new Date(selectedDateTo);
      filtered = filtered.filter(app => {
        if (!app.joiningDate) return false;
        const joiningDate = new Date(app.joiningDate);
        return joiningDate <= toDate;
      });
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      switch (sortBy) {
        case "name":
          aValue = a.candidate?.name ?? "";
          bValue = b.candidate?.name ?? "";
          break;
        case "employeeId":
          aValue = a.employeeId ?? "";
          bValue = b.employeeId ?? "";
          break;
        case "companyName":
          aValue = a.job?.companyName ?? "";
          bValue = b.job?.companyName ?? "";
          break;
        case "joiningDate":
          aValue = a.joiningDate ? new Date(a.joiningDate).getTime() : 0;
          bValue = b.joiningDate ? new Date(b.joiningDate).getTime() : 0;
          break;
        case "isClaimed":
          aValue = a.isClaimed ? 1 : 0;
          bValue = b.isClaimed ? 1 : 0;
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [applications, selectedDateFrom, selectedDateTo, sortBy, sortOrder]);

  const totals = useMemo(() => {
    const toNumber = (val: any): number => {
      if (val === null || val === undefined) return 0;
      const n = Number(val);
      return isNaN(n) ? 0 : n;
    };

    return filteredApplications.reduce(
      (acc, app) => {
        const received = toNumber(app.job?.commissionReceived ?? (app as any).commissionReceived);
        const payable = toNumber(app.job?.commissionPayable ?? (app as any).commissionPayable);
        const claimed = toNumber(app.commClaimed ?? (app.isClaimed ? payable : 0));

        acc.received += received;
        acc.payable += payable;
        acc.claimed += claimed;
        return acc;
      },
      { received: 0, payable: 0, claimed: 0 }
    );
  }, [filteredApplications]);
  
  const displayTotals = {
    received: payoutSummary?.commReceived ?? totals.received,
    payable: payoutSummary?.commPayable ?? totals.payable,
    claimed: payoutSummary?.commClaimed ?? totals.claimed,
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className,
  }) => (
    <th
      className={cn(
        'px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10',
        className
      )}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  const getStatusColor = (status: boolean) => {
    return status
      ? 'bg-green-100 text-green-600 border-green-600 hover:border-green-800 hover:bg-white hover:text-green-800'
      : 'bg-rose-100 text-rose-800 border-rose-300';
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-3 sm:px-6 py-4 flex">
        <div>
          <Button
            variant="ghost"
            size="sm"
            className="p-1 sm:p-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <div className="w-full">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  Tenure Approved Candidates
                </h1>
                <p className="text-xs sm:text-sm text-gray-500">
                  Manage tenure-approved candidates
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search Candidates..."
                  value={searchInputValue}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 w-full sm:w-64 border-gray-300"
                />
              </div>
              <Select
                value={pagination.limit.toString()}
                onValueChange={(value) => {
                  setPagination((prev) => ({
                    ...prev,
                    limit: Number(value),
                    page: 1,
                  }));
                }}
              >
                <SelectTrigger className="w-24 h-10 rounded-lg border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
              <ExcelDownloadButton
                getData={getAppointmentsForExcel}
                filename="tenure-approved-candidates-report"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <SearchableSelect
              options={claimStatusOptions}
              value={selectedClaimStatus}
              onValueChange={(v) => {
                setSelectedClaimStatus(v);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              placeholder="Claim Status"
              className="w-full sm:w-48 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              icon={<CheckCircle className="h-4 w-4 text-gray-500" />}
            />
            <Input
              type="date"
              value={selectedDateFrom}
              onChange={(e) => {
                setSelectedDateFrom(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              title="From Date"
            />
            <Input
              type="date"
              value={selectedDateTo}
              onChange={(e) => {
                setSelectedDateTo(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              title="To Date"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchInputValue("");
                setSearchTerm("");
                setSelectedJob("all");
                setSelectedRecruiter("all");
                setSelectedDateFrom("");
                setSelectedDateTo("");
                setSelectedClaimStatus("all");
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="h-10 px-3 md:px-4 rounded-lg border-gray-200 hover:bg-blue-50 hover:border-blue-300 text-sm font-medium text-gray-700 hover:text-blue-600 shadow-sm w-full sm:w-auto"
            >
              <Filter className="h-4 w-4 mr-2" />
              <span>Clear</span>
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showCandidateDetailsModal} onOpenChange={setShowCandidateDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl no-scrollbar">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="h-12 w-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                {selectedCandidateForDetails?.name.charAt(0).toUpperCase()}
              </div>
              {selectedCandidateForDetails?.name}
            </DialogTitle>
          </DialogHeader>
          {isLoadingDetails ? (
            <div className="text-center py-12">
              <div className="animate-spin inline-block h-10 w-10 border-4 border-orange-600 border-t-transparent rounded-full mb-4"></div>
              <p className="text-lg text-gray-600">Loading candidate details...</p>
            </div>
          ) : detailsError ? (
            <div className="text-center py-12">
              <p className="text-lg text-red-600">{detailsError}</p>
            </div>
          ) : selectedCandidateForDetails ? (
            <div className="space-y-6 p-0">
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="h-5 w-5 text-orange-600" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium text-gray-900">{selectedCandidateForDetails.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-medium text-gray-900">{selectedCandidateForDetails.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Father's Name</p>
                        <p className="font-medium text-gray-900">{selectedCandidateForDetails.fatherName || "N/A"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <UserCheck className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Gender</p>
                        <p className="font-medium text-gray-900">{selectedCandidateForDetails.gender || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-medium text-gray-900">
                          {selectedCandidateForDetails.currentLocationDetails?.city || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Baby className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Date of Birth</p>
                        <p className="font-medium text-gray-900">
                          {selectedCandidateForDetails.dateOfBirth
                            ? new Date(selectedCandidateForDetails.dateOfBirth).toLocaleDateString("en-GB")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Aadhar Number</p>
                        <p className="font-medium text-gray-900">
                          {selectedCandidateForDetails.aadharNumber || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <GraduationCap className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Highest Qualification</p>
                        <p className="font-medium text-gray-900">
                          {selectedCandidateForDetails.highestQualification || "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Languages className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Spoken Languages</p>
                        <p className="font-medium text-gray-900">
                          {selectedCandidateForDetails.spokenLanguages &&
                          selectedCandidateForDetails.spokenLanguages.length > 0
                            ? selectedCandidateForDetails.spokenLanguages.join(", ")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Experience</p>
                        <p className="font-medium text-gray-900">
                          {selectedCandidateForDetails.totalExperienceYears !== undefined &&
                          selectedCandidateForDetails.totalExperienceMonths !== undefined
                            ? `${selectedCandidateForDetails.totalExperienceYears} years ${selectedCandidateForDetails.totalExperienceMonths} months`
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Award className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Skills</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {Array.isArray(selectedCandidateForDetails.skills) &&
                          selectedCandidateForDetails.skills.length > 0 ? (
                            selectedCandidateForDetails.skills.slice(0, 3).map((skill, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {skill}
                              </Badge>
                            ))
                          ) : (
                            <span className="font-medium text-gray-900">N/A</span>
                          )}
                          {Array.isArray(selectedCandidateForDetails.skills) &&
                            selectedCandidateForDetails.skills.length > 3 && (
                              <Badge variant="secondary" className="text-xs">
                                +{selectedCandidateForDetails.skills.length - 3} more
                              </Badge>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-orange-600" />
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div className="flex items-center gap-3">
                      <Building className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Job Category</p>
                        <p className="font-medium text-gray-900">
                          {Array.isArray(selectedCandidateForDetails.preferredJobCategories) &&
                          selectedCandidateForDetails.preferredJobCategories.length > 0
                            ? selectedCandidateForDetails.preferredJobCategories.join(", ")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="h-4 w-4 text-gray-500" />
                      <div>
                        <p className="text-sm text-gray-600">Employment Type</p>
                        <p className="font-medium text-gray-900">
                          {Array.isArray(selectedCandidateForDetails.preferredEmploymentTypes) &&
                          selectedCandidateForDetails.preferredEmploymentTypes.length > 0
                            ? selectedCandidateForDetails.preferredEmploymentTypes.join(", ")
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-orange-600" />
                  Application Status
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Current Status</p>
                    <Badge
                      className={`${getStatusBadgeColor(
                        applications.find((app) => app.candidate.id === selectedCandidateForDetails.id)?.currentStatus
                          ?.status ?? "",
                      )} border font-medium px-3 py-1`}
                    >
                      {applications.find((app) => app.candidate.id === selectedCandidateForDetails.id)?.currentStatus
                        ?.status ?? "N/A"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Sourced By</p>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {applications.find((app) => app.candidate.id === selectedCandidateForDetails.id)?.sourcedBy
                          ?.username ?? "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={showEmploymentModal} onOpenChange={setShowEmploymentModal}>
        <DialogContent className="max-w-2xl rounded-3xl border-0 shadow-2xl">
          <div className="relative">
            <button
              onClick={() => setShowEmploymentModal(false)}
              className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="border-b-2 border-orange-500 pb-4 mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Employment Details</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Employee Id <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Enter Employee Id"
                  value={employmentForm.employeeId}
                  onChange={(e) => setEmploymentForm((prev) => ({ ...prev, employeeId: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Position Name <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Enter Position Name"
                  value={employmentForm.positionName}
                  onChange={(e) => setEmploymentForm((prev) => ({ ...prev, positionName: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Salary <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Enter Salary"
                  value={employmentForm.salary}
                  onChange={(e) => setEmploymentForm((prev) => ({ ...prev, salary: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commission Receivable <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Enter Commission Receivable"
                  value={employmentForm.commission}
                  onChange={(e) => setEmploymentForm((prev) => ({ ...prev, commission: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Commission Paid <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="Enter Commission Paid"
                  value={employmentForm.commissionPaid}
                  onChange={(e) => setEmploymentForm((prev) => ({ ...prev, commissionPaid: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-8">
              <Button
                onClick={handleEmploymentSubmit}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              >
                Submit
              </Button>
              <Button
                onClick={() => setShowEmploymentModal(false)}
                className="flex-1 bg-white hover:bg-gray-50 text-orange-500 font-medium py-3 px-6 rounded-lg border-2 border-orange-500 transition-colors"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showClaimModal} onOpenChange={setShowClaimModal}>
        <DialogContent className="max-w-md mx-auto rounded-2xl shadow-2xl border-0 bg-white/95 backdrop-blur-sm p-8">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 mb-2">Add Comment for Claim</DialogTitle>
          </DialogHeader>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Comment <span className="text-red-500">*</span></label>
            <Input
              type="text"
              placeholder="Enter your comment..."
              value={claimComment}
              onChange={e => setClaimComment(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            />
          </div>
          <div className="flex gap-3 mt-6">
            <Button
              onClick={handleSubmitClaim}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              disabled={!claimComment.trim()}
            >
              Submit
            </Button>
            <Button
              onClick={() => setShowClaimModal(false)}
              className="flex-1 bg-white hover:bg-gray-50 text-orange-500 font-medium py-3 px-6 rounded-lg border-2 border-orange-500 transition-colors"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="max-w-md mx-auto rounded-2xl shadow-2xl border-0 bg-white/95 backdrop-blur-sm p-8">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 mb-2">Edit Payout Details</DialogTitle>
          </DialogHeader>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Commission Payable</label>
            <Input
              type="text"
              value={payoutForm.commissionPayable}
              onChange={e => setPayoutForm(f => ({ ...f, commissionPayable: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Commission Received</label>
            <Input
              type="text"
              value={payoutForm.commissionReceived}
              onChange={e => setPayoutForm(f => ({ ...f, commissionReceived: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Commission Paid</label>
            <Input
              type="text"
              value={payoutForm.commissionPaid}
              onChange={e => setPayoutForm(f => ({ ...f, commissionPaid: e.target.value }))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Comment <span className="text-red-500">*</span></label>
            <Input
              type="text"
              value={payoutForm.comments}
              onChange={e => setPayoutForm(f => ({ ...f, comments: e.target.value }))}
              placeholder="Enter reason for update..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-colors"
            />
          </div>
          <div className="flex gap-3 mt-6">
            <Button
              onClick={handleSubmitPayout}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              disabled={!payoutForm.comments.trim()}
            >
              Submit
            </Button>
            <Button
              onClick={() => setShowPayoutModal(false)}
              className="flex-1 bg-white hover:bg-gray-50 text-orange-500 font-medium py-3 px-6 rounded-lg border-2 border-orange-500 transition-colors"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex-1 min-h-0 overflow-auto bg-background">
      <div className="hidden lg:block shadow-xl border-0 bg-white/90 backdrop-blur-sm min-w-full" style={{margin:"0px"}}>
          <table className="min-w-full divide-y divide-border">
            <thead style={{ backgroundColor: '#FFF5F2' }}>
              <tr>
                <th className="px-4 py-4 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
                <SortableHeader column="name" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-border">
                  Name
                </SortableHeader>
                <SortableHeader column="employeeId" className="bg-[#FFF5F2] min-w-32">Employee ID</SortableHeader>
                <SortableHeader column="companyName" className="bg-[#FFF5F2] min-w-48">Company Name</SortableHeader>
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Company Location</th>
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Position</th>
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Salary</th>
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Comm. Received</th>
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Comm. Payable</th>
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Comm. Claimed</th>
                <SortableHeader column="joiningDate" className="bg-[#FFF5F2] min-w-40">Joining Date</SortableHeader>
              
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Sourced By</th>
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Led By</th>
                <SortableHeader column="isClaimed" className="bg-[#FFF5F2] min-w-40">Is Claimed</SortableHeader>
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Update Claim Status</th>
                <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:bg-muted/50 transition-colors sticky top-0 bg-[#FFF5F2] z-10">Action</th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-border">
              {isLoading ? (
                <>
                  {[...Array(10)].map((_, index) => (
                    <tr key={`shimmer-${index}`} className="bg-white divide-y divide-border">
                      {/* Empty column */}
                      <td className="px-4 py-4"></td>
                      {/* Name */}
                      <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border">
                        <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                      </td>
                      {/* Employee ID */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-20 h-4"></div>
                      </td>
                      {/* Company Name */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-40 h-4"></div>
                      </td>
                      {/* Company Location */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-32 h-4"></div>
                      </td>
                      {/* Position */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-28 h-4"></div>
                      </td>
                      {/* Salary */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Comm. Received */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-20 h-4"></div>
                      </td>
                      {/* Comm. Payable */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-20 h-4"></div>
                      </td>
                      {/* Comm. Claimed */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-16 h-4"></div>
                      </td>
                      {/* Joining Date */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Led By */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-24 h-4"></div>
                      </td>
                      {/* Is Claimed */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded-full w-24 h-6"></div>
                      </td>
                      {/* Update Claim Status */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-28 h-8"></div>
                      </td>
                      {/* Action */}
                      <td className="px-4 py-4">
                        <div className="animate-pulse bg-gray-200 rounded w-16 h-8"></div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : error ? (
                <tr>
                  <td colSpan={14} className="px-6 py-12 text-center">
                    <p className="text-lg text-red-600">{error}</p>
                  </td>
                </tr>
              ) : filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-6 py-12 text-center">
                    <p className="text-lg text-gray-600">No candidates found.</p>
                  </td>
                </tr>
              ) : (
                filteredApplications
                  .slice(0,filteredApplications.length)
                  .map((app) => (
                    <tr
                      key={app.applicationId}
                      className={cn('hover:bg-muted/30 transition-colors cursor-pointer')}
                      onClick={() => handleCandidateNameClick(app)}
                    >
                      <td className="px-4 py-4"></td>
                      <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-border">
                        <span
                          className="font-medium transition-colors cursor-pointer text-foreground hover:text-primary"
                          onClick={() => handleCandidateNameClick(app)}
                        >
                          {app.candidate?.name ?? "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-foreground">{app.employeeId}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{app.job?.companyName || "N/A"}</td>
                      <td className="px-4 py-4 text-sm text-foreground max-w-48 truncate">{app.candidate?.currentLocationDetails?.city || "N/A"}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{app.job?.title || "N/A"}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{app.job?.salary || "N/A"}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{app.job?.commissionReceived || "N/A"}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{app.job?.commissionPayable || "N/A"}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{app.isClaimed ? (app.commClaimed ?? 0) : 0}</td>
                      <td className="px-4 py-4 text-sm text-muted-foreground">
                        {app.joiningDate ? new Date(app.joiningDate).toLocaleDateString("en-GB") : "N/A"}
                      </td>

                      <td className="px-4 py-4 text-sm text-foreground">{app.sourcedBy?.username || "N/A"}</td>
                      <td className="px-4 py-4 text-sm text-foreground">{app.ledBy?.username || "N/A"}</td>
                      <td className="px-4 py-4 text-sm text-foreground">
                        <Badge className={getStatusColor(app.isClaimed ?? false)}>
                          {app.isClaimed ? 'Claimed' : 'Not Claimed'}
                        </Badge>
                      </td>
                      <td className="px-4 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="rounded-xl px-4 py-2 font-medium">
                              {app.isClaimed ? 'Claimed' : 'Not Claimed'} <ChevronDown className="ml-2 h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="rounded-xl shadow-xl border-0 bg-white/95 backdrop-blur-sm">
                            <DropdownMenuItem
                              onClick={(e) => handleClaimClick(e, app.applicationId, "Claimed")}
                              className="cursor-pointer hover:bg-green-50 px-4 py-2 text-green-700"
                            >
                              Claimed
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => handleClaimClick(e, app.applicationId, "Not Claimed")}
                              className="cursor-pointer hover:bg-red-50 px-4 py-2 text-red-700"
                            >
                              Not Claimed
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                      <td className="px-4 py-4">
                        <Button
                          onClick={(e) => handleEditPayoutClick(e, app)}
                          variant="outline"
                          size="sm"
                          className="rounded-xl px-4 py-2 font-medium"
                        >
                          <Edit className="h-4 w-4 mr-2" /> Edit
                        </Button>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
      </div>

      <div className="lg:hidden space-y-4 sm:space-y-6">
        {isLoading ? (
          <>
            {[...Array(5)].map((_, index) => (
              <div
                key={`shimmer-mobile-${index}`}
                className="bg-white border border-gray-100 rounded-lg p-4 sm:p-6"
              >
                {/* Header with avatar and name */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="animate-pulse bg-gray-200 rounded-full w-12 h-12"></div>
                    <div>
                      <div className="animate-pulse bg-gray-200 rounded w-32 h-4 mb-2"></div>
                      <div className="animate-pulse bg-gray-200 rounded w-24 h-3"></div>
                    </div>
                  </div>
                  <div className="animate-pulse bg-gray-200 rounded-full w-20 h-6"></div>
                </div>

                {/* Content rows */}
                <div className="grid grid-cols-1 gap-3 mb-4">
                  {[...Array(11)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                     。<div className="animate-pulse bg-gray-200 rounded w-20 h-3"></div>
                      <div className="animate-pulse bg-gray-200 rounded w-full h-3"></div>
                    </div>
                  ))}
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2">
                  <div className="animate-pulse bg-gray-200 rounded w-32 h-8"></div>
                  <div className="animate-pulse bg-gray-200 rounded w-24 h-8"></div>
                </div>
              </div>
            ))}
          </>
        ) : filteredApplications
          .slice((pagination.page - 1) * pagination.limit, pagination.page * pagination.limit)
          .map((app) => (
            <div
              key={app.applicationId}
              className="bg-white border border-gray-100 rounded-lg p-4 sm:p-6 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg shadow-sm">
                    {app.candidate?.name ? app.candidate.name.charAt(0).toUpperCase() : "N"}
                  </div>
                  <div>
                    <h3
                      className="font-semibold text-gray-900 text-base sm:text-lg cursor-pointer hover:text-orange-600 transition-colors duration-200"
                      onClick={() => handleCandidateNameClick(app)}
                    >
                      {app.candidate?.name ?? "N/A"}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {app.job?.title || "N/A"}
                    </p>
                  </div>
                </div>
                <Badge
                  className={`${getStatusBadgeColor(app.currentStatus?.status ?? "")} border font-medium px-2 py-1 text-xs shadow-sm`}
                >
                  {app.currentStatus?.status ?? "N/A"}
                </Badge>
              </div>
              <div className="grid grid-cols-1 gap-3 mb-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Name:</span>
                  <span className="text-sm text-gray-900">{app.candidate?.name ?? "N/A"}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Employee ID:</span>
                  <span className="text-sm text-gray-900">{app.employeeId}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Company Name:</span>
                  <span className="text-sm text-gray-900">{app.job?.companyName || "N/A"}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Company Location:</span>
                  <span className="text-sm text-gray-900">{app.candidate?.currentLocationDetails?.city || "N/A"}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Position:</span>
                  <span className="text-sm text-gray-900">{app.job?.title || "N/A"}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Salary:</span>
                  <span className="text-sm text-gray-900">{app.job?.salary || "N/A"}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Comm. Received:</span>
                  <span className="text-sm text-gray-900">{app.job?.commissionReceived || "N/A"}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Comm. Payable:</span>
                  <span className="text-sm text-gray-900">{app.job?.commissionPayable || "N/A"}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Comm. Claimed:</span>
                  <span className="text-sm text-gray-900">{app.isClaimed ? (app.commClaimed ?? 0) : 0}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Joining Date:</span>
                  <span className="text-sm text-gray-900">
                    {app.joiningDate ? new Date(app.joiningDate).toLocaleDateString("en-GB") : "N/A"}
                  </span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Sourced By:</span>
                  <span className="text-sm text-gray-900">{app.sourcedBy?.username || "N/A"}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Led By:</span>
                  <span className="text-sm text-gray-900">{app.ledBy?.username || "N/A"}</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-600">Is Claimed:</span>
                  <span className={`text-sm ${app.isClaimed ? 'text-green-700' : 'text-red-700'}`}>
                    {app.isClaimed ? 'Claimed' : 'Not Claimed'}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-xl px-4 py-2 font-medium">
                      {app.isClaimed ? 'Claimed' : 'Not Claimed'} <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="rounded-xl shadow-xl border-0 bg-white/95 backdrop-blur-sm">
                    <DropdownMenuItem
                      onClick={(e) => handleClaimClick(e, app.applicationId, "Claimed")}
                      className="cursor-pointer hover:bg-green-50 px-4 py-2 text-green-700"
                    >
                      Claimed
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => handleClaimClick(e, app.applicationId, "Not Claimed")}
                      className="cursor-pointer hover:bg-red-50 px-4 py-2 text-red-700"
                    >
                      Not Claimed
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  onClick={(e) => handleEditPayoutClick(e, app)}
                  variant="outline"
                  size="sm"
                  className="rounded-xl px-4 py-2 font-medium"
                >
                  <Edit className="h-4 w-4 mr-2" /> Edit
                </Button>
              </div>
            </div>
          ))}
      </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between border-t border-gray-200 bg-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex-shrink-0 gap-3 sm:gap-4">
        <div className="text-[11px] sm:text-xs text-gray-600 font-normal text-center sm:text-left">

          Showing {filteredApplications.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}{' '}
          to {' '} {Math.min(pagination.page * pagination.limit, pagination.totalResults)} {' '}
          of {pagination.totalResults} candidates
          <div className="mt-1 sm:mt-2 text-[11px] sm:text-xs text-gray-700 font-normal flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1">
            <span><span className="font-semibold">Total:</span>{' '}<span className="text-gray-500">Comm. Received:</span> <span className="font-semibold">{displayTotals.received.toLocaleString('en-IN')}</span></span>
            <span><span className="text-gray-500">Comm. Payable:</span> <span className="font-semibold">{displayTotals.payable.toLocaleString('en-IN')}</span></span>
            <span><span className="text-gray-500">Comm. Claimed:</span> <span className="font-semibold">{displayTotals.claimed.toLocaleString('en-IN')}</span></span>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-wrap justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="rounded-lg sm:rounded-xl border-gray-200 hover:bg-white transition-all duration-200 h-8 w-8 sm:h-10 sm:w-10 p-0"
          >
            <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) =>
            pageNum === 1 ||
            pageNum === pagination.totalPages ||
            (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1) ? (
              <Button
                key={pageNum}
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pageNum)}
                className={`rounded-lg sm:rounded-xl transition-all duration-200 h-8 w-8 sm:h-10 sm:w-10 p-0 text-xs sm:text-sm ${
                  pagination.page === pageNum
                    ? "bg-orange-500 text-white border-orange-500 hover:bg-orange-600"
                    : "border-gray-200 hover:bg-white"
                }`}
              >
                {pageNum}
              </Button>
            ) : pageNum === pagination.page - 2 || pageNum === pagination.page + 2 ? (
              <span key={pageNum} className="text-gray-400 text-xs sm:text-sm">
                ...
              </span>
            ) : null
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
            className="rounded-lg sm:rounded-xl border-gray-200 hover:bg-white transition-all duration-200 h-8 w-8 sm:h-10 sm:w-10 p-0"
          >
            <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </div>
      </div>
      
    </div>
  );
}

export default function JoinedCandidates({ onAddCandidate }: { onAddCandidate?: () => void }) {
  return <PayoutCandidates />;
}